import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, MapPin, MessageCircle, Star } from 'lucide-react';
import SectionTitle from '../SectionTitle';
import ProductCard from '../ProductCard';
import { trackClick } from '../../lib/tracking';
import { fetchProducts, fetchArticles, fetchBranches } from '../../api/services';
import { marketplaces, siteConfig } from '../../mock/data';
import { useSettings } from '../../context/SettingsContext';

function HeadingSection({ data }) {
  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4 lg:px-8">
        <SectionTitle
          eyebrow={data.eyebrow}
          title={data.title}
          description={data.description}
          align={data.align || 'center'}
        />
      </div>
    </section>
  );
}

function RichtextSection({ data }) {
  return (
    <section className="py-12 bg-white">
      <div className="container mx-auto px-4 lg:px-8 max-w-3xl">
        <div className="prose prose-gray max-w-none text-gray-600 leading-relaxed whitespace-pre-line">
          {data.content}
        </div>
      </div>
    </section>
  );
}

function ImageSection({ data }) {
  return (
    <section className="py-12 bg-white">
      <div className="container mx-auto px-4 lg:px-8">
        <figure className="max-w-4xl mx-auto">
          <img src={data.src} alt={data.alt || ''} className="w-full rounded-2xl object-cover" loading="lazy" />
          {data.caption ? <figcaption className="text-center text-sm text-gray-500 mt-3">{data.caption}</figcaption> : null}
        </figure>
      </div>
    </section>
  );
}

function ImageTextSection({ data }) {
  const imageFirst = data.imagePosition !== 'right';
  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4 lg:px-8">
        <div className={`grid lg:grid-cols-2 gap-12 items-center ${imageFirst ? '' : ''}`}>
          <div className={`relative ${imageFirst ? '' : 'lg:order-2'}`}>
            <div className="aspect-square rounded-3xl overflow-hidden bg-gradient-to-br from-rose-50 to-yellow-50 p-4">
              <img src={data.image} alt={data.title} className="w-full h-full object-cover rounded-2xl" loading="lazy" />
            </div>
            {(data.badgeValue || data.badgeLabel) && (
              <div className="absolute -bottom-6 -right-6 bg-japo-yellow rounded-2xl p-6 shadow-xl hidden md:block">
                <div className="text-4xl font-extrabold text-japo-green-dark">{data.badgeValue}</div>
                <div className="text-sm font-semibold text-gray-800">{data.badgeLabel}</div>
              </div>
            )}
          </div>
          <div className={imageFirst ? '' : 'lg:order-1'}>
            {data.eyebrow && (
              <div className="inline-block text-xs font-bold uppercase tracking-[0.2em] mb-3 text-japo-green">{data.eyebrow}</div>
            )}
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-6 tracking-tight leading-tight">{data.title}</h2>
            <p className="text-gray-600 leading-relaxed whitespace-pre-line">{data.body}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeaturesSection({ data }) {
  const items = data.items || [];
  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid md:grid-cols-3 gap-6">
          {items.map((f, i) => {
            const list = typeof f.items === 'string'
              ? f.items.split('\n').map((s) => s.trim()).filter(Boolean)
              : (f.items || []);
            return (
              <div key={i} className="bg-white p-8 rounded-2xl border-2 border-gray-100 hover:border-japo-green card-shadow">
                <h3 className="font-bold text-xl mb-5 text-japo-green">{f.title}</h3>
                <ul className="space-y-3">
                  {list.map((item) => (
                    <li key={item} className="flex items-center gap-3 text-gray-700">
                      <div className="w-6 h-6 rounded-full bg-japo-green flex items-center justify-center flex-shrink-0">
                        <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                      </div>
                      <span className="font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function StatsSection({ data }) {
  const items = data.items || [];
  const banner = data.style !== 'plain';
  return (
    <section className={`py-16 ${banner ? 'bg-japo-green' : 'bg-white'}`}>
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {items.map((s) => (
            <div key={s.label} className="text-center">
              <div className={`text-4xl md:text-5xl font-extrabold mb-2 ${banner ? 'text-japo-yellow' : 'text-japo-green'}`}>{s.value}</div>
              <div className={banner ? 'text-white/80 font-medium' : 'text-gray-600 font-medium'}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaSection({ data }) {
  const { settings } = useSettings();
  const wa = settings.whatsapp_number || siteConfig.whatsappNumber;
  const href = data.buttonUrl || `https://api.whatsapp.com/send?phone=${wa}`;
  return (
    <section className="py-16 bg-japo-green relative overflow-hidden">
      <div className="container mx-auto px-4 lg:px-8 relative">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4 leading-tight">{data.title}</h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">{data.description}</p>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackClick('whatsapp_cta_section')}
            className="inline-flex items-center gap-3 bg-japo-yellow hover:bg-[#D19400] text-gray-900 font-extrabold px-8 py-4 rounded-xl shadow-2xl transition-all text-lg"
          >
            <MessageCircle className="w-5 h-5" />
            {data.buttonText || 'Chat WhatsApp'}
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </div>
    </section>
  );
}

function HtmlSection({ data }) {
  return (
    <section className="py-8">
      <div className="container mx-auto px-4 lg:px-8" dangerouslySetInnerHTML={{ __html: data.html || '' }} />
    </section>
  );
}

function ProductsSection({ data }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    fetchProducts().then((d) => setItems(d.slice(0, Number(data.limit) || 6))).catch(() => {});
  }, [data.limit]);
  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4 lg:px-8">
        <SectionTitle eyebrow={data.eyebrow} title={data.title} description={data.description} />
        {items.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5 md:gap-6">
            {items.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
        <div className="text-center mt-10">
          <Link to="/produk" className="inline-flex items-center gap-2 bg-japo-green text-white font-bold px-7 py-3.5 rounded-xl">
            Lihat Semua Produk <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function ArticlesSection({ data }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    fetchArticles({ limit: Number(data.limit) || 3 }).then(setItems).catch(() => {});
  }, [data.limit]);
  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4 lg:px-8">
        <SectionTitle eyebrow={data.eyebrow} title={data.title} description={data.description} />
        <div className="grid md:grid-cols-3 gap-6">
          {items.map((a) => (
            <Link key={a.id} to={`/artikel/${a.slug}`} className="group bg-white rounded-2xl overflow-hidden card-shadow border border-gray-100">
              <div className="aspect-[16/10] overflow-hidden">
                <img src={a.image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              </div>
              <div className="p-6">
                <h3 className="font-bold text-lg text-gray-900 mb-2 group-hover:text-japo-green line-clamp-2">{a.title}</h3>
                <p className="text-sm text-gray-600 line-clamp-2">{a.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function BranchesSection({ data }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    fetchBranches().then((d) => setItems(d.slice(0, Number(data.limit) || 3))).catch(() => {});
  }, [data.limit]);
  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4 lg:px-8">
        <SectionTitle eyebrow={data.eyebrow} title={data.title} description={data.description} />
        <div className="grid md:grid-cols-3 gap-6">
          {items.map((b) => (
            <div key={b.id} className="bg-white rounded-2xl overflow-hidden border border-gray-100 card-shadow">
              <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                <img src={b.image} alt={b.title} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-6">
                <h3 className="font-bold text-lg text-gray-900 mb-2">{b.title}</h3>
                <p className="text-sm text-gray-600 mb-4 flex gap-2">
                  <MapPin className="w-4 h-4 text-japo-green flex-shrink-0 mt-0.5" />
                  <span>{b.address}</span>
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function MarketplaceSection({ data }) {
  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionTitle eyebrow={data.eyebrow} title={data.title} description={data.description} align="left" />
            <div className="grid grid-cols-2 gap-4 mt-4">
              {marketplaces.map((m) => (
                <a key={m.name} href={m.url} target="_blank" rel="noopener noreferrer" className={`${m.bg} rounded-2xl p-5 border-2 border-transparent hover:border-japo-green flex flex-col items-center gap-2 card-shadow text-center`}>
                  <img src={m.logo} alt={m.name} className="w-full max-w-[140px] h-24 object-contain" loading="lazy" />
                  <span className="text-xs text-gray-700 font-medium leading-snug px-1">{m.cta || `Belanja di ${m.name}`}</span>
                </a>
              ))}
            </div>
          </div>
          {data.image && (
            <div className="aspect-square max-w-md mx-auto bg-gradient-to-br from-rose-50 to-yellow-50 rounded-3xl p-8">
              <img src={data.image} alt="" className="w-full h-full object-cover rounded-2xl" loading="lazy" />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

const RENDERERS = {
  heading: HeadingSection,
  richtext: RichtextSection,
  image: ImageSection,
  image_text: ImageTextSection,
  features: FeaturesSection,
  stats: StatsSection,
  cta: CtaSection,
  html: HtmlSection,
  products: ProductsSection,
  articles: ArticlesSection,
  branches: BranchesSection,
  marketplace: MarketplaceSection,
};

export default function SectionRenderer({ sections = [] }) {
  if (!sections?.length) return null;
  return (
    <>
      {sections.filter((s) => s && s.visible !== false).map((section) => {
        const Comp = RENDERERS[section.type];
        if (!Comp) return null;
        return <Comp key={section.id} data={section.data || {}} />;
      })}
    </>
  );
}

export function SectionListPreview({ sections = [] }) {
  return (
    <div className="space-y-2">
      {sections.map((s, i) => (
        <div key={s.id || i} className="text-xs text-gray-500 flex items-center gap-2">
          <Star className="w-3 h-3 text-japo-yellow" />
          <span className="font-semibold text-gray-700">{s.type}</span>
          {s.visible === false && <span className="text-red-500">(hidden)</span>}
        </div>
      ))}
    </div>
  );
}
